import { AnimationEvent, useCallback, useState } from 'react';

/**
 * Floats a login field's label while the browser has autofilled it (CNA-2074).
 *
 * Chrome fills saved credentials as the page loads but withholds the values from scripts until the
 * user interacts with the page, so the form's `value` stays '' and the label rests on top of the
 * filled text. MUI does try to catch this: InputBase plays a `mui-auto-fill` animation on
 * `:-webkit-autofill` and marks the field filled when it starts. On a controlled field that is
 * undone at once — FormControl's context is memoised on `filled`, so the change hands InputBase a
 * new `checkDirty`, its controlled-value effect reruns with `value: ''`, and the field reads as
 * empty again.
 *
 * So the form keeps its own flag, driven by the two animations MUI already runs on every input:
 * `mui-auto-fill` when the browser fills it, `mui-auto-fill-cancel` when that stops (on mount, and
 * when the user edits or clears an autofilled value).
 *
 * `shrink` is `true` or `undefined`, never `false`: `false` would pin the label down even while
 * the field is focused or typed in, and `undefined` leaves those cases to MUI. TextField also sets
 * the outlined border's notch from `InputLabelProps.shrink`, so the gap opens with the label.
 *
 * `onAnimationStart` goes in `inputProps`, which MUI spreads after its own handler, so it replaces
 * InputBase's rather than adding to it. That is fine: on a controlled field that handler only
 * produced the state this hook now owns.
 */
export function useAutofillLabel() {
  const [autofilled, setAutofilled] = useState(false);

  const onAnimationStart = useCallback((event: AnimationEvent<HTMLInputElement>) => {
    if (event.animationName === 'mui-auto-fill') setAutofilled(true);
    else if (event.animationName === 'mui-auto-fill-cancel') setAutofilled(false);
  }, []);

  return { shrink: autofilled || undefined, onAnimationStart };
}
